import { FileViewerService } from "./../../../../../../../services/file-viewer.service";
// Angular Core
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  AfterViewInit,
  Injector,
  ApplicationRef,
  createComponent,
  EnvironmentInjector,
  EmbeddedViewRef,
} from "@angular/core";
import { AsyncPipe, CommonModule } from "@angular/common";

// DevExtreme
import {
  DxContextMenuModule,
  DxPopoverModule,
  DxButtonModule,
} from "devextreme-angular";

// Application Types
import {
  ProjectHubBoardListItem,
  ProjectHubSpark,
  SparkAnswerItem,
  tagColor,
  WriteSparkAnswerRequest,
} from "../../project-hub.types";

// Application Components
import { SvgIconComponent } from "src/app/components/svg-icon/svg-icon.component";
import { UserAvatarComponent } from "src/app/components/user-avatar/user-avatar.component";
import { AnswerCardComponent } from "../answer-card/answer-card.component";
import { AnswerEditorComponent } from "../answer-editor/answer-editor.component";
import { SparkTagsOverflowComponent } from "../spark-tags-overflow/spark-tags-overflow.component";
import { EmojiPickerComponent } from "src/app/components/emoji-picker/emoji-picker.component";

// Application Services
import { ProjectHubService } from "src/app/services/project-hub.service";
import { ConfigService } from "src/app/services/config.service";
import { SparkHistoryService } from "src/app/pages/grid-connection/project/project-tabs/project-hub/components/spark-history-overlay/spark-history.service";

// Application Pipes and Modules
import { TimeAgoPipe } from "src/app/pipes/time-ago.pipe";
import { SharedPipesModule } from "src/app/pipes/shared.pipes.module";

// Application Globals
import { AppGlobals } from "src/app/app.globals";
import { ProjectHubTruncateService } from "../project-hub-content-truncate.service";
import { Subscription } from "rxjs";
import { EmojiComponent } from "@ctrl/ngx-emoji-mart/ngx-emoji";
import { one } from "devextreme/events";
import { GpdButtonComponent } from "src/app/components/gpd-button/gpd-button.component";
import Color from "@tiptap/extension-color";

interface SparkLikeEvent {
  sparkId: number;
  isLiked: boolean;
}

interface TagMap {
  [key: string]: { id: number; name: string; color?: tagColor };
}

interface MenuAction {
  text: string;
  id: string;
}

@Component({
    selector: "app-spark-card",
    templateUrl: "./spark-card.component.html",
    styleUrls: ["../spark-card.shared.css", "./spark-card.component.css"],
    imports: [
        TimeAgoPipe,
        CommonModule,
        SvgIconComponent,
        DxContextMenuModule,
        AnswerCardComponent,
        AnswerEditorComponent,
        UserAvatarComponent,
        SharedPipesModule,
        AsyncPipe,
        SparkTagsOverflowComponent,
        EmojiPickerComponent,
        EmojiComponent,
        DxPopoverModule,
        DxButtonModule,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SparkCardComponent
  implements OnInit, OnDestroy, OnChanges, AfterViewInit
{
  // Inputs
  @Input() spark!: ProjectHubSpark;
  @Input() selected = false;
  @Input() tags!: TagMap;
  @Input() expanded = false;
  @Input() currentBoardId!: number;

  @ViewChild("sparkContentContainer", { static: false })
  sparkContentContainer: ElementRef<HTMLDivElement>;
  @ViewChild("safeContainer", { static: false })
  safeContainer: ElementRef<HTMLDivElement>;
  @ViewChild("newAnswerEditor") newAnswerEditor?: AnswerEditorComponent;
  // Outputs - consistent naming with 'emit' or verb convention
  @Output() edit = new EventEmitter<ProjectHubSpark>();
  @Output() delete = new EventEmitter<ProjectHubSpark>();
  @Output() pin = new EventEmitter<ProjectHubSpark>();
  @Output() unpin = new EventEmitter<ProjectHubSpark>();
  @Output() fileClick = new EventEmitter<string>();
  @Output() read = new EventEmitter<number>();
  @Output() like = new EventEmitter<SparkLikeEvent>();
  @Output() move = new EventEmitter<{
    spark: ProjectHubSpark;
    targetBoardId: number;
  }>();

  // Content display
  isContentExpanded = false;
  isContentTruncated = false;
  processedContent = "";
  boards: ProjectHubBoardListItem[] = [];

  // Answers
  isAnswerSectionOpen = false;
  answers: SparkAnswerItem[] = [];
  answerCount = 0;

  // Loading states
  isLoadingAnswers = false;
  isSubmittingAnswer = false;

  // Image management
  private readonly imageCache = new Map<string, string>();
  private readonly loadingImageIds = new Set<string>();

  boardSub: Subscription;

  // Reaction management
  reactionEntries: { emoji: string; users: string[] }[] = [];
  selectedReaction: string | null = null;

  // Menu configuration
  private readonly MENU_ACTIONS: MenuAction[] = [
    { text: $localize`:@@edit:Edit`, id: "edit" },
    { text: $localize`:@@history:History`, id: "history" },
    { text: $localize`:@@delete:Delete`, id: "delete" },
    { text: $localize`:@@pin:Pin`, id: "pin" },
    { text: $localize`:@@unpin:Unpin`, id: "unpin" },
  ];

  // Controls whether all liked users are shown in the popover
  showAllLikedUsers = false;

  get displayedLikedUsers(): string[] {
    if (!this.spark || !Array.isArray(this.spark.likedByUsers)) return [];
    return this.showAllLikedUsers
      ? this.spark.likedByUsers
      : this.spark.likedByUsers.slice(0, 3);
  }

  constructor(
    private truncateService: ProjectHubTruncateService,
    private projectHubService: ProjectHubService,
    private cdr: ChangeDetectorRef,
    public configService: ConfigService,
    private sparkHistoryService: SparkHistoryService,
    private appGlobals: AppGlobals,
    private fileViewerService: FileViewerService,
    private envInjector: EnvironmentInjector,
    private appRef: ApplicationRef
  ) {
    this.boardSub = this.projectHubService.boardsSubject.subscribe(
      (boards) => (this.boards = boards)
    );
  }

  async ngOnInit(): Promise<void> {
    this.answerCount = this.getAnswerCountFromSpark();
    await this.processContent();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes["spark"]) {
      await this.processContent();
      this.cdr.detectChanges();
    }
  }

  containsTable(string: string): any {
    const matches = string.match(/<table\b[^>]*>/gi);
    return matches && matches.length > 0;
  }

  ngAfterViewInit(): void {
    // Re-process content after view is initialized to get accurate container width
    setTimeout(async () => await this.processContent(), 0);
  }

  private prepareImageAttributes(img: HTMLImageElement): void {
    const fileId = img.getAttribute("data-file-id");
    if (!fileId) return;

    const cachedUrl = this.imageCache.get(fileId);
    if (cachedUrl) {
      img.setAttribute("src", cachedUrl);
      img.removeAttribute("data-loading");
    } else {
      img.setAttribute("src", "");
      img.setAttribute("data-loading", "true");
      this.loadImageIfNotInProgress(fileId);
    }
    img.setAttribute("data-file-id", fileId);
    // Apply max-width to ALL images (including those in tables)
    img.style.maxWidth = "100%";
  }

  private createImageWrappers(): void {
    if (!this.safeContainer) return;

    const container = this.safeContainer.nativeElement;
    const images = container.querySelectorAll("img[data-file-id]");

    images.forEach((img: HTMLImageElement) => {
      const fileId = img.getAttribute("data-file-id");
      if (!fileId) return;

      // Skip wrapper creation if already wrapped
      if (img.parentElement?.classList.contains("image-wrapper")) {
        return;
      }

      // Skip wrapper creation if image is inside a table
      if (img.closest("table")) {
        return;
      }

      img.style.display = "block";
      img.style.width = "auto";

      const wrapper = document.createElement("div");
      wrapper.className = "image-wrapper";
      wrapper.style.position = "relative";
      wrapper.style.display = "inline-block";
      wrapper.style.border = "1px solid #DFDFDF";
      wrapper.style.borderRadius = "8px";
      wrapper.style.overflow = "hidden";

      // Track expanded state
      wrapper.dataset.expanded = "true";

      img.parentElement.insertBefore(wrapper, img);
      wrapper.appendChild(img);

      // Expand button
      const expandRef = createComponent(GpdButtonComponent, {
        environmentInjector: this.envInjector,
      });

      expandRef.setInput("icon", "sizing");
      expandRef.setInput("iconSize", 24);
      expandRef.setInput("color", "#629D37");
      expandRef.setInput("ngStyle", {
        backgroundColor: "#ffffff",
        border: "1px solid #DFDFDF",
        borderRadius: "8px",
      });

      this.appRef.attachView(expandRef.hostView);
      const expandNode = (expandRef.hostView as EmbeddedViewRef<unknown>)
        .rootNodes[0] as HTMLElement;

      expandNode.style.position = "absolute";
      expandNode.style.top = "6px";
      expandNode.style.right = "6px";
      expandNode.style.cursor = "pointer";

      wrapper.appendChild(expandNode);

      // Footer
      const footer = document.createElement("div");
      footer.className = "image-footer";
      footer.style.display = "flex";
      footer.style.alignItems = "center";
      footer.style.padding = "4px 8px";
      footer.style.background = "#ffffff";
      footer.style.borderRadius = "0 0 4px 4px";
      footer.style.width = "100%";
      footer.style.cursor = "pointer"; // clickable to expand

      const leftIconRef = createComponent(SvgIconComponent, {
        environmentInjector: this.envInjector,
      });

      leftIconRef.setInput("icon", "image");
      leftIconRef.setInput("color", "#629D37");
      leftIconRef.setInput("size", 32);

      this.appRef.attachView(leftIconRef.hostView);
      const leftIconNode = (leftIconRef.hostView as EmbeddedViewRef<unknown>)
        .rootNodes[0] as HTMLElement;
      footer.appendChild(leftIconNode);

      const filename = this.filenameLookup(fileId);

      const title = document.createElement("span");
      title.textContent = filename;
      title.style.paddingLeft = "8px";
      title.style.fontSize = "14px";
      title.style.fontWeight = "700";
      title.style.color = "var(--color-grey-900)";
      footer.appendChild(title);

      wrapper.appendChild(footer);

      //
      // ---- Collapse/Expand Logic ----
      //

      const collapse = () => {
        wrapper.dataset.expanded = "false";
        img.style.display = "none";
        expandNode.style.display = "none";
        wrapper.style.height = footer.offsetHeight + "px";
      };

      const expand = () => {
        wrapper.dataset.expanded = "true";
        img.style.display = "block";
        expandNode.style.display = "block";
        wrapper.style.height = "auto";
      };

      // clicking top-right button collapses/expands
      expandNode.addEventListener("click", () => {
        if (wrapper.dataset.expanded === "true") {
          collapse();
        } else {
          expand();
        }
      });

      // clicking footer expands if collapsed
      footer.addEventListener("click", () => {
        if (wrapper.dataset.expanded === "false") {
          expand();
        }
      });

      expand();
    });

    this.cdr.detectChanges();
  }

  filenameLookup(fileId: string): string {
    const fileEntry = this.spark.content.files
      ? this.spark.content.files[fileId]
      : null;
    return fileEntry ? fileEntry.fileName : "Unknown Filename";
  }

  ngOnDestroy(): void {
    this.cleanupImageCache();
    this.boardSub.unsubscribe();
  }

  // Lifecycle and utility methods
  private cleanupImageCache(): void {
    this.imageCache.forEach((url) => URL.revokeObjectURL(url));
    this.imageCache.clear();
  }

  async processContent(): Promise<void> {
    if (!this.sparkContentContainer) return;
    const parser = new DOMParser();
    let html = this.preserveEmptyParagraphs(this.spark.content.text || "");

    if (!html) {
      this.processedContent = "";
      return;
    }

    // Handle truncation if containers are available
    if (!this.isContentExpanded && !this.expanded) {
      const truncated = this.truncateService.truncateContent(
        this.sparkContentContainer,
        html
      );
      this.isContentTruncated = truncated.wasTruncated;
      const doc = parser.parseFromString(truncated.content, "text/html");

      html = doc.body.innerHTML;
      html = this.preserveEmptyParagraphs(html);
    }

    const doc = parser.parseFromString(html, "text/html");

    // Only prepare image attributes in the parsed DOM
    const images = doc.querySelectorAll("img[data-file-id]");
    const documentDivs = doc.querySelectorAll("div[data-file-id]");
    const checkboxes = doc.querySelectorAll("input");

    images.forEach((img) =>
      this.prepareImageAttributes(img as HTMLImageElement)
    );
    documentDivs.forEach((div) =>
      this.processDocumentElement(div as HTMLDivElement)
    );

    // Disable checkboxes when viewing a spark

    checkboxes.forEach((checkbox) => {
      (checkbox as HTMLInputElement).disabled = true;
    });

    this.processedContent = doc.body.innerHTML;
    this.getReactionEntries();

    // After content is rendered, create wrappers in the actual DOM
    if (this.safeContainer) {
      setTimeout(() => this.createImageWrappers(), 0);
    }
  }

  private preserveEmptyParagraphs(html: string): string {
    return html
      .replace(/<p><\/p>/g, '<p class="empty-line">&nbsp;</p>')
      .replace(/<p>\s*<\/p>/g, '<p class="empty-line">&nbsp;</p>');
  }

  private processDocumentElement(div: HTMLDivElement): void {
    const fileId = div.getAttribute("data-file-id");
    if (!fileId) return;

    // Ensure the data-file-id attribute is preserved on div elements
    // This is critical for document attachments that use div placeholders
    div.setAttribute("data-file-id", fileId);
  }

  private loadImageIfNotInProgress(fileId: string): void {
    if (!this.loadingImageIds.has(fileId)) {
      this.loadingImageIds.add(fileId);
      this.loadAuthenticatedImage(fileId);
    }
  }

  // Image loading methods
  private async loadAuthenticatedImage(fileId: string): Promise<void> {
    try {
      const blob = await this.projectHubService.GetSparkAttachment(fileId);
      const imageUrl = URL.createObjectURL(blob);
      this.imageCache.set(fileId, imageUrl);
      this.loadingImageIds.delete(fileId);

      this.processContent();
      this.cdr.detectChanges();
    } catch (error) {
      this.loadingImageIds.delete(fileId);
      this.setErrorImage(fileId);
    }
  }

  private setErrorImage(fileId: string): void {
    setTimeout(() => {
      const imgElement = document.querySelector(
        `img[data-file-id="${fileId}"]`
      ) as HTMLImageElement;
      if (imgElement) {
        imgElement.src = "assets/error-placeholder.svg";
        imgElement.removeAttribute("data-loading");
        this.cdr.detectChanges();
      }
    }, 0);
  }

  onContextMenuItemClick(event: any) {
    const action = (event.itemData as any).id;
    this.executeMenuAction(action);
  }

  private executeMenuAction(actionId: string): void {
    switch (actionId) {
      case "edit":
        this.edit.emit(this.spark);
        break;
      case "history":
        this.navigateToHistory();
        break;
      case "delete":
        this.delete.emit(this.spark);
        break;
      case "pin":
        this.pin.emit(this.spark);
        break;
      case "unpin":
        this.unpin.emit(this.spark);
        break;
      default:
        // Handle move to board actions (board IDs are passed as action IDs)
        if (actionId.startsWith("move-to-")) {
          const targetBoardId = parseInt(actionId.replace("move-to-", ""));
          this.move.emit({ spark: this.spark, targetBoardId });
        }
        break;
    }
  }

  private navigateToHistory(): void {
    if (!this.spark.id) return;

    // Open spark history overlay
    this.sparkHistoryService.openSparkHistory(this.spark.id);
  }

get visibleMenuItems(): any[] {
  const baseItems = this.MENU_ACTIONS.filter((item) => {
    if (item.id === "pin" && this.spark.pinned) return false;
    if (item.id === "unpin" && !this.spark.pinned) return false;
    // Anyone can edit or delete now
    return true;
  });

  // Add "Move to" submenu if there are other boards available
  if (this.boards.length > 1) {
    const moveToItem = {
      text: $localize`:@@moveTo:Move to`,
      id: "move-to",
      items: this.boards
        .filter((board) => board.id !== this.currentBoardId)
        .map((board) => ({
          text: board.name,
          id: `move-to-${board.id}`,
        })),
    };

    // Insert "Move to" item before the last item (typically Delete)
    const insertIndex = baseItems.length > 0 ? baseItems.length - 1 : 0;
    baseItems.splice(insertIndex, 0, moveToItem);
  }

  return baseItems;
}

  async submitAnswer(content: string): Promise<void> {
    if (this.isSubmittingAnswer) return;

    this.isSubmittingAnswer = true;
    try {
      const request: WriteSparkAnswerRequest = {
        sparkId: this.spark.id!,
        answer: {
          content,
        },
      };
      await this.projectHubService.WriteSparkAnswer(
        request.sparkId,
        request.answer
      );
      this.newAnswerEditor?.clear();
      this.answerCount++;
      await this.loadAnswers();
    } catch (error) {
      AppGlobals.ShowErrorMessage(
        $localize`:@@answerSubmissionError:Error submitting answer. Please try again.`
      );
    } finally {
      this.isSubmittingAnswer = false;
    }
  }

  handleAnswerEdit(answer: SparkAnswerItem) {
    // Move the editing answer to the top of the list
    this.answers = this.answers.sort((a, b) => {
      if (a.id === answer.id) return -1;
      if (b.id === answer.id) return 1;
      return (
        new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime()
      );
    });
  }

  async handleAnswerUpdate(answer: SparkAnswerItem) {
    try {
      const request: WriteSparkAnswerRequest = {
        sparkId: this.spark.id!,
        answer: {
          id: answer.id,
          content: answer.content,
        },
      };
      const result = await this.projectHubService.WriteSparkAnswer(
        request.sparkId,
        request.answer
      );
      if (result.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
        return;
      }
      await this.loadAnswers();
    } catch (error) {
      AppGlobals.ShowErrorMessage(
        $localize`:@@internalServerError:Internal server error`
      );
    }
  }

  async handleAnswerDelete(answer: SparkAnswerItem) {
    if (!answer.id) return;

    try {
      const result = await this.projectHubService.WriteSparkAnswer(answer.id, {
        ...answer,
        isDeleted: true,
      });
      if (result.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
        return;
      }
      await this.loadAnswers();
    } catch (error) {
      AppGlobals.ShowErrorMessage(
        $localize`:@@internalServerError:Internal server error`
      );
    }
  }

  private async loadAnswers(): Promise<void> {
    if (!this.spark.id || this.isLoadingAnswers) return;

    this.isLoadingAnswers = true;

    try {
      const result = await this.projectHubService.GetSparkAnswers(
        this.spark.id
      );
      this.answerCount = result.answers.length;
      this.answers = result.answers;
    } catch (error) {
      AppGlobals.ShowErrorMessage(
        $localize`:@@answersLoadError:Error loading answers. Please try again.`
      );
    } finally {
      this.isLoadingAnswers = false;
      this.cdr.detectChanges();
    }
  }

  private getAnswerCountFromSpark(): number {
    // If spark has answerCount property (from API), use it
    // Otherwise, use the length of loaded answers
    return (this.spark as any).answerCount || this.answers.length;
  }

  private async handleFileElementClick(fileId: string , fileName: string) {
    this.markSparkAsRead();
    this.fileClick.emit(fileId);

    try {
      this.fileViewerService.openOrDownloadFile("spark", fileId, fileName , "");
    } catch (error) {
      AppGlobals.ShowErrorMessage(
        $localize`:@@fileDownloadError:File could not be downloaded. Please try again.`
      );
    } finally {
      this.appGlobals.HideGlobalLoader();
    }
  }

  // Spark interaction methods
  markSparkAsRead(): void {
    if (!this.spark.id || !this.spark.isNew) return;
    this.read.emit(this.spark.id);
  }

  toggleLike(): void {
    if (!this.spark.id) return;
    const newLikedState = !this.spark.liked;
    this.spark.liked = newLikedState;
    this.spark.likeCount = newLikedState
      ? this.spark.likeCount + 1
      : this.spark.likeCount - 1;
    this.spark.isNew = false;

    // Ensure likedByUsers array exists
    if (!Array.isArray(this.spark.likedByUsers)) {
      this.spark.likedByUsers = [];
    }
    const currentUserEmail = AppGlobals.LoggedInUserData.email;
    if (newLikedState) {
      // Add email if not present
      if (!this.spark.likedByUsers.includes(currentUserEmail)) {
        this.spark.likedByUsers.push(currentUserEmail);
      }
    } else {
      // Remove email if present
      this.spark.likedByUsers = this.spark.likedByUsers.filter(
        (e) => e !== currentUserEmail
      );
    }

    this.cdr.detectChanges();
    this.like.emit({
      sparkId: this.spark.id,
      isLiked: newLikedState,
    });
  }

  toggleAnswerSection(): void {
    this.markSparkAsRead();
    this.isAnswerSectionOpen = !this.isAnswerSectionOpen;

    if (this.isAnswerSectionOpen && this.answers.length === 0) {
      this.loadAnswers();
    }
  }

  toggleContent(): void {
    this.markSparkAsRead();
    this.isContentExpanded = !this.isContentExpanded;

    // First render the expanded/collapsed content
    this.cdr.detectChanges();

    // Wait for Angular to apply height & overflow rules
    setTimeout(() => {
      this.processContent();
      this.cdr.detectChanges();
    }, 0);
  }

  onContentClick(event: MouseEvent): void {
    this.markSparkAsRead();

    const target = event.target as HTMLElement;
    const element = target.closest("[data-file-id]");
    if (element) {
      const fileId = element.getAttribute("data-file-id");
      const fileName = this.filenameLookup(fileId);
      event.preventDefault();
      event.stopPropagation();
      this.handleFileElementClick(fileId!, fileName);
    }
  }

  getReactionEntries(): { emoji: string; users: string[] }[] {
    if (!this.spark.reactions) {
      return [];
    }

    this.reactionEntries = Object.entries(this.spark.reactions).map(
      ([emoji, users]) => ({
        emoji,
        users,
      })
    );

    this.selectedReaction = this.reactionEntries.find((x) =>
      x.users.includes(AppGlobals.LoggedInUserData.email)
    )?.emoji;
  }

  onReactionClick(emoji: string): void {
    if (!this.spark.id) return;

    let previousState = this.spark.reactions;

    const currentUserEmail = AppGlobals.LoggedInUserData.email;
    let isAlreadyReacted = false;
    for (const key in this.spark.reactions) {
      if (this.spark.reactions.hasOwnProperty(key)) {
        const users = this.spark.reactions[key];
        const index = users.indexOf(currentUserEmail);
        if (index > -1) {
          isAlreadyReacted = true;
          this.spark.reactions[key].splice(index, 1);
          if (this.spark.reactions[key].length === 0) {
            delete this.spark.reactions[key];
          }
          break;
        }
      }
    }

    if (this.selectedReaction !== emoji) {
      if (!this.spark.reactions![emoji]) {
        this.spark.reactions![emoji] = [];
      }
      this.spark.reactions![emoji].push(currentUserEmail);
      this.selectedReaction = emoji;
    }
    this.getReactionEntries();

    this.projectHubService
      .ReactToSpark(this.spark.id, emoji, !isAlreadyReacted)
      .then((result) => {
        if (result.hasError) {
          AppGlobals.ShowErrorMessage(result.feedback);
          this.spark.reactions = previousState;
          this.getReactionEntries();
          this.cdr.detectChanges();
          return;
        }
      })
      .catch(() => {
        AppGlobals.ShowErrorMessage(
          $localize`:@@internalServerError:Internal server error`
        );
      });

    this.markSparkAsRead();
  }

  /**
   * Returns the translated tooltip for the author date span.
   */
  getLastEditedTooltip(): string {
    const created = this.spark?.dateCreated
      ? new TimeAgoPipe().transform(this.spark.dateCreated)
      : "";
    if (this.spark?.lastModifiedDate) {
      const lastEdited = new TimeAgoPipe().transform(
        this.spark.lastModifiedDate
      );
      const lastModifiedBy =
        this.configService.userMap$.value?.[this.spark.lastModifiedBy] ||
        this.spark.lastModifiedBy;
      return $localize`:@@lastEditedTooltip:${created}, last edited ${lastEdited} by ${lastModifiedBy}`;
    }
  }

  onHidingLikePopover(e: any) {
    e.cancel = new Promise((cancel) => {
      setTimeout(() => {
        const popupContainer = e.component.content().parentElement;
        const isHover = popupContainer.parentNode.matches(":hover");
        if (isHover) {
          one(popupContainer, "mouseleave", () => {
            cancel(false);
          });
        } else {
          cancel(false);
        }
      }, 100);
    });
  }

  /**
   * Returns a safe ID for emoji-based selectors.
   */
  public safeEmojiId(emoji: string): string {
    // Replace all non-alphanumeric characters with underscores
    return "spark-reaction-" + emoji.replace(/[^a-zA-Z0-9]/g, "_");
  }
}
